import { useEffect, useState } from 'react';
import { X, Share, Plus, Download, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  isStandalone,
  getPlatform,
  isInstallDismissed,
  markInstallDismissed,
} from '@/lib/pwa';

const SHOW_DELAY_MS = 4000;

export default function PWAInstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [platform, setPlatform] = useState('unknown');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isStandalone()) return;
    if (isInstallDismissed()) return;

    const plat = getPlatform();
    setPlatform(plat);

    // Listen for the native install prompt (Android Chrome / desktop Chrome / Edge)
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    };

    // App was installed — hide banner
    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
      markInstallDismissed();
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    // iOS Safari has no beforeinstallprompt — show banner with manual instructions
    let iosTimer;
    if (plat === 'ios-safari') {
      iosTimer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const handleDismiss = () => {
    markInstallDismissed();
    setVisible(false);
  };

  const handleInstall = async () => {
    if (platform === 'ios-safari') {
      setShowIosHelp(true);
      return;
    }
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          setVisible(false);
        }
        setDeferredPrompt(null);
      } catch {
        setVisible(false);
      }
    }
  };

  if (!visible) return null;

  const isIos = platform === 'ios-safari';

  return (
    <>
      <div
        data-testid="pwa-install-banner"
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-1.5rem)] max-w-md animate-in slide-in-from-bottom-4 fade-in"
      >
        <div className="rounded-2xl border border-teal-200/60 bg-white shadow-[0_12px_40px_-8px_rgba(13,148,136,0.35)] p-4 flex items-start gap-3">
          <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white">
            <Home className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-stone-900 leading-tight">
              Install Kinsy on your home screen
            </p>
            <p className="text-xs text-stone-600 mt-0.5 leading-relaxed">
              {isIos
                ? 'One tap from your home screen — and you’ll get push notifications too.'
                : 'Faster access, full-screen, and push notifications for new posts & events.'}
            </p>
            <div className="flex items-center gap-2 mt-3">
              <Button
                data-testid="pwa-install-button"
                size="sm"
                onClick={handleInstall}
                className="bg-teal-600 hover:bg-teal-700 text-white h-8 px-3 text-xs font-medium rounded-lg"
              >
                {isIos ? (
                  <>
                    <Share className="w-3.5 h-3.5 mr-1.5" />
                    How to install
                  </>
                ) : (
                  <>
                    <Download className="w-3.5 h-3.5 mr-1.5" />
                    Install app
                  </>
                )}
              </Button>
              <Button
                data-testid="pwa-install-dismiss"
                size="sm"
                variant="ghost"
                onClick={handleDismiss}
                className="h-8 px-2 text-xs text-stone-500 hover:text-stone-700"
              >
                Not now
              </Button>
            </div>
          </div>
          <button
            data-testid="pwa-install-close"
            onClick={handleDismiss}
            aria-label="Dismiss install prompt"
            className="shrink-0 text-stone-400 hover:text-stone-700 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <Dialog open={showIosHelp} onOpenChange={setShowIosHelp}>
        <DialogContent
          data-testid="pwa-ios-instructions"
          className="max-w-sm rounded-2xl"
        >
          <DialogHeader>
            <DialogTitle className="text-stone-900">Add Kinsy to your Home Screen</DialogTitle>
            <DialogDescription className="text-stone-600">
              Just two taps in Safari and you’re done.
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 mt-2">
            <li className="flex items-start gap-3">
              <span className="shrink-0 w-7 h-7 rounded-full bg-teal-100 text-teal-700 font-semibold flex items-center justify-center text-sm">
                1
              </span>
              <p className="text-sm text-stone-700 flex items-center flex-wrap gap-1.5">
                Tap the
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-stone-100 border border-stone-200 text-stone-800 font-medium">
                  <Share className="w-3.5 h-3.5" /> Share
                </span>
                button at the bottom of Safari.
              </p>
            </li>
            <li className="flex items-start gap-3">
              <span className="shrink-0 w-7 h-7 rounded-full bg-teal-100 text-teal-700 font-semibold flex items-center justify-center text-sm">
                2
              </span>
              <p className="text-sm text-stone-700 flex items-center flex-wrap gap-1.5">
                Scroll and choose
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-stone-100 border border-stone-200 text-stone-800 font-medium">
                  <Plus className="w-3.5 h-3.5" /> Add to Home Screen
                </span>
                .
              </p>
            </li>
            <li className="flex items-start gap-3">
              <span className="shrink-0 w-7 h-7 rounded-full bg-teal-100 text-teal-700 font-semibold flex items-center justify-center text-sm">
                3
              </span>
              <p className="text-sm text-stone-700">
                Tap <span className="font-semibold">Add</span> — Kinsy will appear on your home screen like any other app.
              </p>
            </li>
          </ol>
          <div className="flex justify-end gap-2 mt-3">
            <Button
              data-testid="pwa-ios-instructions-close"
              variant="ghost"
              onClick={() => {
                setShowIosHelp(false);
                handleDismiss();
              }}
              className="text-stone-600"
            >
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
